import { Margin, usePDF } from "react-to-pdf";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from "@mui/material";
import PrintIcon from "@mui/icons-material/Print";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs, { Dayjs } from "dayjs";
import { useEffect, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { axiosInstance } from "../../../../utils/networking";
import {
  REFERRAL_PIPELINE_STATUSES,
  pipelineStatusColors,
  pipelineStatusLabels,
} from "../../../common/ReferralStatusChip";

const PRIMARY = "#9D2362";
const CARD_BORDER = "1px solid #ececf0";

const CATEGORY_COLORS = [
  PRIMARY,
  "#3F51B5",
  "#2e7d32",
  "#fdb814",
  "#00838f",
  "#8e24aa",
  "#e64a19",
  "#5d4037",
  "#455a64",
  "#c2185b",
];

// ---- Small presentational helpers (visual language shared with the B2B
// report — cloned, not extracted, to keep that stable report untouched) ------

const SectionCard = ({ children, sx }: { children: any; sx?: any }) => (
  <Paper
    elevation={0}
    sx={{
      p: 3,
      borderRadius: "16px",
      border: CARD_BORDER,
      backgroundColor: "white",
      ...sx,
    }}
  >
    {children}
  </Paper>
);

const SectionTitle = ({
  children,
  subtitle,
}: {
  children: any;
  subtitle?: string;
}) => (
  <Box sx={{ mb: 2 }}>
    <Typography
      variant="h6"
      sx={{
        fontWeight: 700,
        color: "#2c2c34",
        borderLeft: `4px solid ${PRIMARY}`,
        pl: 1.5,
        lineHeight: 1.25,
      }}
    >
      {children}
    </Typography>
    {subtitle ? (
      <Typography variant="body2" sx={{ color: "#8a8a93", pl: 1.5, mt: 0.5 }}>
        {subtitle}
      </Typography>
    ) : null}
  </Box>
);

const StatCard = ({
  label,
  value,
  accent,
}: {
  label: string;
  value: any;
  accent: string;
}) => (
  <Paper
    elevation={0}
    sx={{
      flex: "1 1 150px",
      p: 2.5,
      borderRadius: "16px",
      border: CARD_BORDER,
      borderTop: `3px solid ${accent}`,
      backgroundColor: "white",
      textAlign: "center",
    }}
  >
    <Typography
      sx={{
        fontSize: "2rem",
        fontWeight: 800,
        color: "#2c2c34",
        lineHeight: 1.1,
      }}
    >
      {value}
    </Typography>
    <Typography
      variant="caption"
      sx={{
        color: "#8a8a93",
        textTransform: "uppercase",
        letterSpacing: "0.5px",
        fontWeight: 600,
      }}
    >
      {label}
    </Typography>
  </Paper>
);

const ChartCard = ({
  title,
  subtitle,
  height,
  children,
}: {
  title: string;
  subtitle?: string;
  height: number | string;
  children: any;
}) => (
  <SectionCard sx={{ flex: "1 1 380px", minWidth: 0 }}>
    <SectionTitle subtitle={subtitle}>{title}</SectionTitle>
    <Box sx={{ height }}>
      <ResponsiveContainer width="100%" height="100%">
        {children}
      </ResponsiveContainer>
    </Box>
  </SectionCard>
);

const CompletionBar = ({ rate }: { rate: number }) => {
  const pct = Math.round((rate || 0) * 100);
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        justifyContent: "flex-end",
      }}
    >
      <Box sx={{ width: 64 }}>
        <LinearProgress
          variant="determinate"
          value={pct}
          sx={{
            height: 7,
            borderRadius: 4,
            backgroundColor: "#eef0f2",
            "& .MuiLinearProgress-bar": {
              backgroundColor: "#2e7d32",
              borderRadius: 4,
            },
          }}
        />
      </Box>
      <Typography variant="body2" sx={{ minWidth: 36, textAlign: "right" }}>
        {pct}%
      </Typography>
    </Box>
  );
};

// Sortable partner table with one funnel column per pipeline status, colored
// to match ReferralStatusChip.
const PartnerTable = ({ partners }: { partners: any[] }) => {
  const [sortKey, setSortKey] = useState("total");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");

  const columns: { key: string; label: string; numeric?: boolean }[] = [
    { key: "name", label: "Partner" },
    { key: "category", label: "Category" },
    ...REFERRAL_PIPELINE_STATUSES.map((status) => ({
      key: `status:${status}`,
      label: pipelineStatusLabels[status] || status,
      numeric: true,
    })),
    { key: "total", label: "Total", numeric: true },
    { key: "distinctClients", label: "Clients", numeric: true },
    { key: "completionRate", label: "Completion Rate", numeric: true },
  ];

  const getSortValue = (partner: any, key: string) => {
    if (key.startsWith("status:")) {
      return partner.byStatus[key.slice("status:".length)] || 0;
    }
    const value = partner[key];
    return typeof value === "string" ? value.toLowerCase() : value || 0;
  };

  const sorted = [...partners].sort((a, b) => {
    const av = getSortValue(a, sortKey);
    const bv = getSortValue(b, sortKey);
    if (av < bv) return sortDir === "asc" ? -1 : 1;
    if (av > bv) return sortDir === "asc" ? 1 : -1;
    return 0;
  });

  const handleSort = (key: string) => {
    if (sortKey === key) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortKey(key);
      setSortDir("desc");
    }
  };

  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          {columns.map((col) => (
            <TableCell
              key={col.key}
              align={col.numeric ? "right" : "left"}
              sortDirection={sortKey === col.key ? sortDir : false}
              sx={
                col.key.startsWith("status:")
                  ? {
                      color:
                        pipelineStatusColors[col.key.slice("status:".length)],
                      fontWeight: 600,
                    }
                  : undefined
              }
            >
              <TableSortLabel
                active={sortKey === col.key}
                direction={sortKey === col.key ? sortDir : "asc"}
                onClick={() => handleSort(col.key)}
              >
                {col.label}
              </TableSortLabel>
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {sorted.map((partner: any) => (
          <TableRow key={partner.id} hover>
            <TableCell>
              <Typography sx={{ fontWeight: 600 }}>{partner.name}</Typography>
              <Chip
                label={partner.kind === "provider" ? "Provider" : "Resource"}
                size="small"
                variant="outlined"
                sx={{ height: 18, fontSize: "0.65rem", mt: 0.25 }}
              />
            </TableCell>
            <TableCell>{partner.category}</TableCell>
            {REFERRAL_PIPELINE_STATUSES.map((status) => (
              <TableCell
                key={status}
                align="right"
                sx={{
                  color: partner.byStatus[status]
                    ? pipelineStatusColors[status]
                    : "#c5c5cc",
                  fontWeight: partner.byStatus[status] ? 700 : 400,
                }}
              >
                {partner.byStatus[status] || 0}
              </TableCell>
            ))}
            <TableCell align="right" sx={{ fontWeight: 700 }}>
              {partner.total}
            </TableCell>
            <TableCell align="right">{partner.distinctClients}</TableCell>
            <TableCell align="right" sx={{ width: 160 }}>
              <CompletionBar rate={partner.completionRate} />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

const EcosystemReport = () => {
  const [report, setReport] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState<Dayjs | null>(null);
  const [endDate, setEndDate] = useState<Dayjs | null>(null);

  const fetchReport = (start: Dayjs | null, end: Dayjs | null) => {
    setLoading(true);
    let url = `/reports/ecosystem`;
    const params: string[] = [];
    if (start) params.push(`startDate=${start.format("YYYY-MM-DD")}`);
    if (end) params.push(`endDate=${end.format("YYYY-MM-DD")}`);
    if (params.length > 0) url = url + `?` + params.join("&");
    axiosInstance
      .get(url)
      .then((response: any) => {
        setReport(response.data);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchReport(null, null);
  }, []);

  const { toPDF, targetRef } = usePDF({
    filename: "Ecosystem_Report.pdf",
    page: { margin: Margin.SMALL, format: "letter" },
    overrides: {
      canvas: {
        removeContainer: false,
      },
    },
  });

  const handleCsvDownload = () => {
    if (!report) return;
    let csvContent: any[] = [];
    let csvHeading = [
      "Partner",
      "Kind",
      "Category",
      ...REFERRAL_PIPELINE_STATUSES.map(
        (status) => pipelineStatusLabels[status] || status,
      ),
      "Total",
      "Distinct Clients",
      "Completion Rate",
    ];
    csvContent.push(csvHeading);

    for (const partner of report.partners) {
      let csvAnswer = [];
      csvAnswer.push(partner.name);
      csvAnswer.push(partner.kind);
      csvAnswer.push(partner.category);
      for (const status of REFERRAL_PIPELINE_STATUSES) {
        csvAnswer.push(partner.byStatus[status] || 0);
      }
      csvAnswer.push(partner.total);
      csvAnswer.push(partner.distinctClients);
      csvAnswer.push(`${Math.round((partner.completionRate || 0) * 100)}%`);
      csvContent.push(csvAnswer);
    }

    let csvString = ``;
    for (const row of csvContent) {
      let rowString = `\n`;
      for (const item of row) {
        rowString = rowString + `"${item}",`;
      }
      csvString = csvString + rowString;
    }

    const blob = new Blob([csvString], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `ecosystem_report.csv`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);

    axiosInstance.post("/export_log", {
      export_type: "ecosystem_report",
      record_count: report.partners.length,
      filters: {
        report: "Ecosystem",
        startDate: startDate ? startDate.format("YYYY-MM-DD") : null,
        endDate: endDate ? endDate.format("YYYY-MM-DD") : null,
      },
    });
  };

  const rangeLabel =
    startDate || endDate
      ? `${startDate ? startDate.format("MMM D, YYYY") : "Beginning"} – ${
          endDate ? endDate.format("MMM D, YYYY") : "Today"
        }`
      : "All time";

  const toolbar = (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        mb: 3,
        borderRadius: "16px",
        border: CARD_BORDER,
        display: "flex",
        flexWrap: "wrap",
        gap: 2,
        alignItems: "center",
        justifyContent: "space-between",
      }}
    >
      <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", flexWrap: "wrap" }}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            label="Start Date"
            value={startDate}
            onChange={(date) => setStartDate(date)}
            disableFuture={true}
            slotProps={{ textField: { size: "small" } }}
          />
          <DatePicker
            label="End Date"
            value={endDate}
            onChange={(date) => setEndDate(date)}
            shouldDisableDate={(date: Dayjs) =>
              startDate !== null && date < startDate
            }
            slotProps={{ textField: { size: "small" } }}
          />
        </LocalizationProvider>
        <Button
          variant="contained"
          onClick={() => fetchReport(startDate, endDate)}
          sx={{ backgroundColor: PRIMARY, "&:hover": { backgroundColor: "#7A1B4D" } }}
        >
          Apply
        </Button>
        <Button
          variant="text"
          onClick={() => {
            setStartDate(null);
            setEndDate(null);
            fetchReport(null, null);
          }}
        >
          Reset
        </Button>
      </Box>
      <Box sx={{ display: "flex", gap: 1.5 }}>
        <Button
          variant="outlined"
          startIcon={<PrintIcon />}
          onClick={() => {
            toPDF();
            axiosInstance.post("/export_log", {
              export_type: "report_pdf",
              record_count: report ? report.partners.length : 0,
              filters: {
                report: "Ecosystem",
                startDate: startDate ? startDate.format("YYYY-MM-DD") : null,
                endDate: endDate ? endDate.format("YYYY-MM-DD") : null,
              },
            });
          }}
        >
          Print
        </Button>
        <Button
          variant="outlined"
          startIcon={<FileDownloadIcon />}
          onClick={handleCsvDownload}
        >
          CSV
        </Button>
      </Box>
    </Paper>
  );

  const pageSx = {
    backgroundColor: "#f6f7f9",
    p: { xs: 2, md: 3 },
    minHeight: "100%",
  };

  if (loading && !report) {
    return (
      <Box
        sx={{
          ...pageSx,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: 400,
        }}
      >
        <CircularProgress sx={{ color: PRIMARY }} />
      </Box>
    );
  }

  if (!report) {
    return <Box sx={pageSx}>{toolbar}</Box>;
  }

  const partnerChartData = report.partners.slice(0, 10).map((partner: any) => ({
    name: partner.name,
    Total: partner.total,
    Completed: partner.byStatus["completed"] || 0,
  }));

  const categoryData = report.categories.map((category: any) => ({
    name: category.category,
    value: category.total,
  }));

  const statCards = [
    { label: "Total Referrals", value: report.totals.referrals, accent: PRIMARY },
    { label: "Completed", value: report.totals.completed, accent: "#2e7d32" },
    {
      label: "Completion Rate",
      value: `${Math.round(report.totals.completionRate * 100)}%`,
      accent: "#2e7d32",
    },
    {
      label: "Partners Engaged",
      value: report.totals.partners,
      accent: "#3F51B5",
    },
  ];

  const isEmpty = report.totals.referrals === 0;

  return (
    <Box sx={pageSx}>
      {toolbar}

      <Box ref={targetRef}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 800, color: "#2c2c34" }}>
            Ecosystem Report
          </Typography>
          <Typography variant="body2" sx={{ color: "#8a8a93", mt: 0.5 }}>
            Referrals by partner organization and category · {rangeLabel}
          </Typography>
        </Box>

        {isEmpty ? (
          <SectionCard sx={{ textAlign: "center", py: 8 }}>
            <Typography variant="h6" sx={{ color: "#8a8a93" }}>
              No referrals found for this period
            </Typography>
            <Typography variant="body2" sx={{ color: "#aaa", mt: 1 }}>
              Try widening the date range or resetting the filter.
            </Typography>
          </SectionCard>
        ) : (
          <Stack spacing={3}>
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
              {statCards.map((card) => (
                <StatCard
                  key={card.label}
                  label={card.label}
                  value={card.value}
                  accent={card.accent}
                />
              ))}
            </Box>

            <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
              <ChartCard
                title="Top Partners by Volume"
                subtitle="Providers and resources, ranked by referrals received"
                height={280}
              >
                <BarChart
                  data={partnerChartData}
                  margin={{ top: 20, bottom: 70, right: 20 }}
                >
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis
                    dataKey="name"
                    interval={0}
                    angle={-30}
                    tick={{ fontSize: 10, textAnchor: "end" }}
                  />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Legend />
                  <Bar
                    dataKey="Total"
                    fill={PRIMARY}
                    isAnimationActive={false}
                    radius={[4, 4, 0, 0]}
                  >
                    <LabelList dataKey="Total" position="top" />
                  </Bar>
                  <Bar
                    dataKey="Completed"
                    fill="#2e7d32"
                    isAnimationActive={false}
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              </ChartCard>

              <ChartCard
                title="Referrals by Category"
                subtitle='Answers "how many clients were connected to funding resources"'
                height={280}
              >
                <PieChart>
                  <Pie
                    data={categoryData}
                    dataKey="value"
                    nameKey="name"
                    innerRadius={55}
                    outerRadius={90}
                    paddingAngle={2}
                    label={(entry: any) => entry.value}
                    isAnimationActive={false}
                  >
                    {categoryData.map((entry: any, index: number) => (
                      <Cell
                        key={entry.name}
                        fill={CATEGORY_COLORS[index % CATEGORY_COLORS.length]}
                      />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ChartCard>
            </Box>

            <SectionCard>
              <SectionTitle subtitle="Every partner with the full pipeline funnel — completion rate counts referrals that progressed past Referred">
                By Partner
              </SectionTitle>
              <PartnerTable partners={report.partners} />
            </SectionCard>
          </Stack>
        )}
      </Box>
    </Box>
  );
};

export default EcosystemReport;
